import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { inputClass, labelClass, ghostButtonClass, primaryButtonClass } from "@/components/base/fieldStyles";

interface ChangePasswordModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  displayName?: string;
  requireCurrent?: boolean;
  onClose: () => void;
  onSave: (password: string) => Promise<boolean>;
}

// Modal para cambiar la contraseña (la propia o la de un asesor, según cómo se use).
const ChangePasswordModal = ({
  open,
  title,
  subtitle,
  displayName,
  onClose,
  onSave,
}: ChangePasswordModalProps) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirm("");
      setShow(false);
      setError("");
      setSaving(false);
    }
  }, [open]);

  const submit = async () => {
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSaving(true);
    const ok = await onSave(password);
    setSaving(false);
    if (!ok) {
      setError("No se pudo cambiar la contraseña. Intenta de nuevo.");
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      icon="ri-lock-password-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} disabled={saving} className={primaryButtonClass}>
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />}{" "}
            Guardar contraseña
          </button>
        </>
      }
    >
      {displayName && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-accent-500/25 bg-background-200/30 px-3.5 py-3">
          <span className="w-9 h-9 rounded-full bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0 font-semibold">
            {displayName.charAt(0).toUpperCase() || "?"}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 truncate">{displayName}</p>
            {subtitle && <p className="text-[11px] text-foreground-500">{subtitle}</p>}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className={labelClass}>Nueva contraseña</label>
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              autoComplete="new-password"
              className={`${inputClass} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-900 cursor-pointer"
              aria-label={show ? "Ocultar" : "Mostrar"}
            >
              <i className={show ? "ri-eye-off-line" : "ri-eye-line"} />
            </button>
          </div>
        </div>
        <div>
          <label className={labelClass}>Confirmar contraseña</label>
          <input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repite la contraseña"
            autoComplete="new-password"
            className={inputClass}
          />
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default ChangePasswordModal;